import { Box, Container, IconButton, Typography } from '@mui/material';
import type { InventoryResource } from '@repo/api-gw-sdk';

import ButtonWithIcon from '@/components/buttons/buttonWithIcon';
import { ActionsBar, ActionsToolBar } from '@/components/shared/actionsBar';
import { AuthorizationTooltip } from '@/components/tooltips/authorizationTooltip';
import {
  InventoryResourceActionsLevels,
  type ResourceAction,
} from '@/data/inventory/actions';
import { isDateValid } from '@/utils/dateTime';
import { DATE_TIME_FORMAT, dayjs } from '@/utils/dayjs';

interface InstanceActionsBarProps {
  actions: Partial<Record<InventoryResourceActionsLevels, ResourceAction[]>>;
  entity: InventoryResource;
}

export default function InstanceActionsBar(props: InstanceActionsBarProps) {
  const { entity, actions } = props;

  let lastBackup = entity.latestSnapshotTime;
  if (!isDateValid(lastBackup)) {
    lastBackup = undefined;
  }

  const primaryActions = actions[InventoryResourceActionsLevels.MainPrimary];
  const secondaryActions =
    actions[InventoryResourceActionsLevels.MainSecondary];

  return (
    <ActionsBar
      position='static'
      className='mb-0 px-[40px] py-[16px]'
      sx={{
        borderTop: '1px solid var(--border-color)',
      }}
    >
      <Container maxWidth='xl' disableGutters>
        <ActionsToolBar className='flex justify-between px-[0px] min-h-0'>
          <Typography variant='subtitle1'>
            {isDateValid(lastBackup)
              ? `Last backed up on ${dayjs.utc(lastBackup).format(DATE_TIME_FORMAT)} UTC`
              : 'The last backup date is unknown'}
          </Typography>
          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            {secondaryActions?.map((action) => (
              <AuthorizationTooltip
                key={action.title || action.tooltip}
                isAuthorized={action.isAuthorized}
                title={action.tooltip}
              >
                <IconButton
                  className='my-auto align-middle'
                  data-testid={action.id}
                  onClick={() => {
                    action.execute(props.entity);
                  }}
                  size='small'
                  color='primary'
                  disabled={action.disabled?.()}
                >
                  <i className={action.icon} />
                </IconButton>
              </AuthorizationTooltip>
            ))}
            {primaryActions?.map((action) => (
              <AuthorizationTooltip
                key={action.title || action.tooltip}
                isAuthorized={action.isAuthorized}
              >
                <ButtonWithIcon
                  className='mx-[12px] last:mr-[0px]'
                  data-testid={action.id}
                  onClick={() => action.execute(entity)}
                  variant={action.title ? 'outlined' : 'text'}
                  disabled={action.disabled?.()}
                  text={action.title ?? ''}
                  icon={action.icon}
                />
              </AuthorizationTooltip>
            ))}
          </Box>
        </ActionsToolBar>
      </Container>
    </ActionsBar>
  );
}
